import { AdminRouteContainer } from "components/general/AdminRouteContainer";
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { createAuthenticatedAxiosInstance, patchOrder, showOrder } from "api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import { isSuccessStatusCode } from "helpers/httpResponse";
import { IOrderDetails } from "interfaces/IOrder";
import { UpperSection } from "./orderDetails/UpperSection";
import { ContentSection } from "./orderDetails/ContentSection";
import { FooterSection } from "./orderDetails/FooterSection";
import { UpdateOrderModal } from "./UpdateOrderModal";
import { faCheckCircle, faExclamationTriangle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { IConfirmModal, ICancelContinueModal } from "interfaces/IModal";
import { ModalHandler } from "components/general/ModalHandler";
import { IUpdateOrder } from "interfaces/IUpdateOrder";

/**
 * The order details component
 */
export const OrderDetails = () => {
    const location = useLocation();
    const initialOrder = location.state || {};
    const [order, setOrder] = useState<IOrderDetails>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const { token } = useAuthStore();
    const axiosInstance = createAuthenticatedAxiosInstance(token);
    const initialModal = {
        show: false,
        onHide: () => { },
        onConfirm: () => { },
        title: "Atención",
        message: "",
        icon: faExclamationTriangle,
        iconColor: "text-danger"
    };
    const [modal, setModal] = useState<IConfirmModal | ICancelContinueModal>(initialModal);

    /**
     * Fetch the order information
     */
    const fetchOrder = useCallback(async () => {
        const { data, statusCode } = await showOrder(axiosInstance, initialOrder.id);
        if (!isSuccessStatusCode(statusCode)) {
            return;
        }
        setOrder(data);
    }, [])

    /**
     * handles the update button click
     */
    const onUpdateClick = () => setShowModal(true);

    /**
     * shows the confirm modal after clicking save on the update order modal
     */
    const showConfirmModal = (updateBody: IUpdateOrder) => {
        setModal({
            ...modal,
            message: "¿Estás seguro de guardar los cambios?",
            show: true,
            onConfirm: () => handleUpdate(updateBody),
            onHide: () => setModal({ ...modal, show: false })
        });
    }

    /**
     * Method that handles the update of the order upon confirming at the modal
     */
    const handleUpdate = async (body: IUpdateOrder) => {
        if (!order)
            return;
        const { data, statusCode, result } = await patchOrder(axiosInstance, order?.id, body);
        if (!isSuccessStatusCode(statusCode)) {
            const errorMessage = result.errors ? Object.values(result.errors).join("\n") : "";
            const errorModal: IConfirmModal = {
                title: "Confirmación",
                message: errorMessage,
                icon: faTimesCircle,
                iconColor: "text-danger",
                onHide: () => setModal({ ...modal, show: false }),
                show: true
            }
            setModal(errorModal);
        }
        if (!data)
            return;
        const successModal: IConfirmModal = {
            show: true,
            title: "Confirmación",
            message: "La orden se ha actualizado con éxito.",
            icon: faCheckCircle,
            iconColor: "text-success",
            onHide: () => setModal({ ...modal, show: false }),
        }
        setModal(successModal);
        setOrder(data.order);
    }

    /**
     * fetch the order details are required on render
     */
    useEffect(() => { fetchOrder(); }, [fetchOrder]);

    return (
        <AdminRouteContainer customClass="align-items-stretch" customMaxWidth={1000} backRoute="/dashboard#pedidos">
            <>
                {order &&
                    <>
                        <UpperSection order={order} onUpdateClick={onUpdateClick} />
                        <ContentSection order={order} />
                        <FooterSection order={order} />
                        <UpdateOrderModal
                            show={showModal}
                            order={order}
                            onHide={() => setShowModal(false)}
                            showConfirm={showConfirmModal}
                        />
                        <ModalHandler modal={modal} />
                    </>
                }
            </>
        </AdminRouteContainer>
    )
}
